import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  Sparkles,
  Calendar,
  Layers,
  Boxes,
  Heart,
  Gift,
  Moon,
  Monitor,
  Frame,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison directe et rapide ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Livraison de cartons {city.aCity} : directe et rapide
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que votre temps est compté et que transporter des
              piles de cartons volumineux dans un véhicule personnel est
              une corvée fatigante, notre service logistique gère tout
              pour vous. Nous livrons l&apos;intégralité de vos commandes
              à plat directement sur le pas de votre porte
              {" "}{city.aCity}, mais aussi dans les communes voisines
              comme Plaisir, Villepreux, Bois-d&apos;Arcy,
              Fontenay-le-Fleury et Saint-Cyr-l&apos;École.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Toutes vos fournitures professionnelles vous sont remises
              dans un état de propreté impeccable, totalement sèches et
              sur un créneau horaire précis fixé selon vos disponibilités
              pour vous éviter toute attente inutile.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Livraison à plat sur le pas de votre porte",
                body: "Plus besoin de charger votre voiture : nous acheminons l'intégralité de la commande chez vous.",
                Icon: Truck,
              },
              {
                title: "Créneau précis selon vos disponibilités",
                body: "Vous fixez l'horaire de réception : nous le respectons à la minute pour ne jamais bloquer votre journée.",
                Icon: Calendar,
              },
              {
                title: "Fournitures propres, sèches et neuves",
                body: "Sous protection, parfaitement saines, prêtes pour un montage à haute résistance.",
                Icon: ShieldCheck,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Déménager en famille ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Déménager en famille
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Impliquez vos enfants sans stress
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Un changement de domicile est une étape de vie majeure qui
            peut perturber les plus jeunes. Transformer les préparatifs
            en un projet ludique et organisé permet de désamorcer
            l&apos;anxiété et de gagner en efficacité.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Le carton secret",
              body: "Confiez à chaque enfant un carton standard personnalisé. Laissez-les le décorer avec des feutres ou des autocollants. Ils pourront y placer eux-mêmes leurs objets fétiches, doudous et jouets préférés, qu'ils ouvriront en priorité dès l'arrivée dans leur nouvelle chambre.",
              Icon: Gift,
            },
            {
              title: "La mission « tri et rangement »",
              body: "Utilisez la phase d'emballage pour faire le tri dans les jouets oubliés et les vêtements trop petits. Expliquez-leur qu'offrir ces affaires à des associations locales des Yvelines permet de faire de la place pour leur future vie.",
              Icon: Heart,
            },
            {
              title: "Le kit de survie pour la première nuit",
              body: "Préparez un carton spécifique pour la famille contenant le nécessaire immédiat : pyjamas, brosses à dents, chargeurs de téléphone, draps, et quelques collations. Cela vous évitera de fouiller dans cinquante boîtes scellées le premier soir alors que tout le monde est épuisé.",
              Icon: Moon,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-[#EEF3F7] rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-les-clayes-sous-bois-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir nos kits famille &amp; appartement
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Objets précieux & High-Tech ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Objets précieux &amp; high-tech
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Sécurisez vos biens de valeur
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le transport de matériel informatique, d&apos;écrans plats,
            d&apos;œuvres d&apos;art ou de collections demande un
            protocole d&apos;emballage strict pour éviter la casse ou les
            rayures micro-superficielles.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Une double barrière de protection",
              body: "Pour vos écrans OLED, consoles de jeux ou tableaux de valeur, n'utilisez jamais le carton à nu. Enveloppez d'abord vos objets dans une couche protectrice de notre film à bulles haute densité. Pour les surfaces ultra-sensibles ou les toiles peintes, ajoutez une sous-couche de papier kraft doux pour éviter que les bulles de plastique ne laissent des marques avec la chaleur du transport.",
              Icon: Monitor,
            },
            {
              title: "Le calage thermique et l'immobilisation",
              body: "Une fois l'objet placé dans son carton renforcé à double cannelure, comblez absolument tous les espaces vides à l'aide de particules de calage ou de papier froissé. Si le carton émet un bruit ou qu'un mouvement est perçu lorsque vous le secouez légèrement, c'est qu'il n'est pas assez rempli. Vos appareils doivent former un bloc unique avec le carton pour résister aux vibrations des routes des Yvelines.",
              Icon: Layers,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-les-clayes-sous-bois-cta-protection"
          >
            <Frame className="h-4 w-4" strokeWidth={2.6} />
            Découvrir nos protections high-tech
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-les-clayes-sous-bois-cta-shop"
          >
            <Sparkles className="h-4 w-4" strokeWidth={2.6} />
            Voir tous les cartons renforcés
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-les-clayes-sous-bois-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
